import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home,
  Building,
  Calendar,
  Camera,
  Settings,
  Menu,
  LogOut,
  User,
  type LucideIcon,
} from 'lucide-react';
import { useAppStore } from '../store';
import { useAdminPendingCount } from '../store/selectors';
import { useAuth } from '../contexts/AuthContext';
import { useViewMode } from '../contexts/ViewModeContext';
import { cn } from '../lib/utils';
import Sidebar from './Sidebar';
import ViewModeToggle from './ViewModeToggle';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { currentUser } = useAppStore();
  const { signOut } = useAuth();
  const { isGuestView } = useViewMode();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const adminPendingCount = useAdminPendingCount();

  type NavItem = {
    name: string;
    href: string;
    icon: LucideIcon;
    hideInGuestView: boolean;
    hideForFamilyMember: boolean;
    badge?: number;
  };

  const navigation: NavItem[] = [
    { name: t('nav.home'), href: '/', icon: Home, hideInGuestView: true, hideForFamilyMember: true },
    { name: t('nav.properties'), href: '/homes', icon: Building, hideInGuestView: false, hideForFamilyMember: false },
    { name: t('nav.myBookings'), href: '/bookings', icon: Calendar, hideInGuestView: false, hideForFamilyMember: false },
    { name: t('nav.brags'), href: '/brag-board', icon: Camera, hideInGuestView: false, hideForFamilyMember: false },
    { name: t('nav.profile'), href: '/profile', icon: User, hideInGuestView: false, hideForFamilyMember: false },
    ...(currentUser?.isAdmin ? [{ name: t('nav.admin'), href: '/admin', icon: Settings, hideInGuestView: true, hideForFamilyMember: true, badge: adminPendingCount }] : []),
  ].filter((item) => {
    if (isGuestView) return !item.hideInGuestView;
    if (currentUser?.isFamilyMember) return !item.hideForFamilyMember;
    return true;
  });

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="flex h-screen bg-transparent overflow-hidden">
      {/* Desktop Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Mobile Header + Drawer - only visible on mobile */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 flex items-center justify-between h-14 px-4 bg-background/60 backdrop-blur-xl border-b border-border/50">
        <div className="flex items-center">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <button className="relative p-2 -ml-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
              <Menu className="h-5 w-5" />
              {adminPendingCount > 0 && (
                <span className="absolute top-1 right-1 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold leading-none ring-2 ring-background">
                  {adminPendingCount > 9 ? '9+' : adminPendingCount}
                </span>
              )}
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0">
            <SheetHeader className="p-4 border-b border-border/50 bg-background/40 backdrop-blur-xl">
              <SheetTitle className="flex items-center gap-2">
                <span className="font-extrabold bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">Holiday Homes der Familie Richner</span>
              </SheetTitle>
            </SheetHeader>

            {/* Mobile navigation */}
            <nav className="p-3 space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                const badge = item.badge ?? 0;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all duration-300 no-underline border',
                      isActive(item.href)
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-500/25 border-white/10'
                        : 'border-transparent text-muted-foreground hover:bg-foreground/5 hover:text-foreground'
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="flex-1">{item.name}</span>
                    {badge > 0 && (
                      <span className={cn(
                        'inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full text-[10px] font-bold leading-none',
                        isActive(item.href) ? 'bg-white/20 text-white' : 'bg-red-500 text-white'
                      )}>
                        {badge > 99 ? '99+' : badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Mobile user section */}
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border bg-card">
              {currentUser && (
                <div className="px-3 py-2 mb-3">
                  <p className="text-sm font-medium text-foreground">{currentUser.name}</p>
                  <p className="text-xs text-muted-foreground">{currentUser.email}</p>
                  <p className="text-xs text-primary mt-1">
                    {currentUser.isAdmin ? 'Admin' : currentUser.isFamily ? 'Family' : 'Guest'}
                  </p>
                </div>
              )}
<button
                onClick={() => {
                  signOut();
                  setMobileMenuOpen(false);
                }}
                className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all"
              >
                <LogOut className="h-5 w-5" />
                <span>{t('nav.signOut')}</span>
              </button>
            </div>
          </SheetContent>
        </Sheet>

          <Link to={isGuestView ? '/homes' : '/'} className="flex items-center gap-2 ml-2 no-underline">
            <span className="text-lg font-extrabold bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">Holiday Homes der Familie Richner</span>
          </Link>
        </div>

        <ViewModeToggle compact />
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Main scrollable content */}
        <main className="flex-1 overflow-y-auto pt-14 lg:pt-0">
          <div className="max-w-7xl mx-auto px-4 py-6 lg:px-8 lg:py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
